import { For, Show } from "solid-js";
import { cn } from "@mealplan/shared";

/** Minimum height/width (in %) of a bar with value > 0, so it remains visible. */
const MIN_VISIBLE_PCT = 6;
const MIN_VISIBLE_PCT_HORIZONTAL = 4;
const FULL_PCT = 100;

export interface BarDatum {
  /** Visible label below/next to the bar. */
  label: string;
  /** Numeric value (>= 0). */
  value: number;
  /** Alternative accessible text; defaults to `label: value`. */
  srLabel?: string;
}

interface BarChartProps {
  data: BarDatum[];
  /** Accessible chart title (for screen readers). */
  caption: string;
  /** Unit suffix for displayed values (e.g. "kcal"). */
  unitSuffix?: string;
  /** Orientation: vertical (column) or horizontal bars. */
  orientation?: "vertical" | "horizontal";
  class?: string;
}

/**
 * Inline, theme-safe, and accessible bar chart (Phase 5, axis 5).
 *
 * - No heavy library: pure CSS/HTML on theme tokens (`bg-accent`,
 *   `text-muted-foreground`, …), adapting to any theme.
 * - Accessible: exposed as `role="img"` with an `aria-label` summarizing the
 *   series, plus an `sr-only` table with raw data for screen
 *   readers. Visual bars are `aria-hidden`.
 * - Respects `prefers-reduced-motion` via `motion-safe:transition-*`.
 */
export function BarChart(props: BarChartProps) {
  const max = () => Math.max(1, ...props.data.map((d) => (Number.isFinite(d.value) ? d.value : 0)));
  const isHorizontal = () => props.orientation === "horizontal";

  const summary = () =>
    `${props.caption}. ${props.data
      .map(
        (d) =>
          `${d.srLabel ?? d.label}: ${d.value}${props.unitSuffix ? ` ${props.unitSuffix}` : ""}`,
      )
      .join("; ")}`;

  return (
    // role="img" + aria-label is the canonical accessible way to expose a
    // chart as a single image with alt text; <img> does not apply.
    // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
    <figure class={cn("w-full", props.class)} role="img" aria-label={summary()}>
      {/* Tabla equivalente para lectores de pantalla. */}
      <table class="sr-only">
        <caption>{props.caption}</caption>
        <tbody>
          <For each={props.data}>
            {(d) => (
              <tr>
                <th scope="row">{d.srLabel ?? d.label}</th>
                <td>
                  {d.value}
                  {props.unitSuffix ? ` ${props.unitSuffix}` : ""}
                </td>
              </tr>
            )}
          </For>
        </tbody>
      </table>

      <Show
        when={isHorizontal()}
        fallback={
          <div aria-hidden="true" class="flex h-40 items-end justify-between gap-2">
            <For each={props.data}>
              {(d) => {
                const pct = () => Math.round((Math.max(0, d.value) / max()) * FULL_PCT);
                return (
                  <div class="flex min-w-0 flex-1 flex-col items-center gap-1">
                    <div class="flex h-32 w-full items-end justify-center">
                      <div
                        class="w-full max-w-9 rounded-t-md bg-accent/80 motion-safe:transition-[height] motion-safe:duration-500"
                        style={{ height: `${Math.max(d.value > 0 ? MIN_VISIBLE_PCT : 0, pct())}%` }}
                        title={`${d.label}: ${d.value}`}
                      />
                    </div>
                    <span class="w-full truncate text-center text-[10px] font-medium text-muted-foreground">
                      {d.label}
                    </span>
                    <span class="text-xs font-semibold tabular-nums">{d.value}</span>
                  </div>
                );
              }}
            </For>
          </div>
        }
      >
        <div aria-hidden="true" class="flex flex-col gap-2">
          <For each={props.data}>
            {(d) => {
              const pct = () => Math.round((Math.max(0, d.value) / max()) * FULL_PCT);
              return (
                <div class="flex items-center gap-3">
                  <span class="w-28 shrink-0 truncate text-xs font-medium text-muted-foreground">
                    {d.label}
                  </span>
                  <div class="h-5 flex-1 overflow-hidden rounded-md bg-accent/10">
                    <div
                      class="h-full rounded-md bg-accent/80 motion-safe:transition-[width] motion-safe:duration-500"
                      style={{
                        width: `${Math.max(d.value > 0 ? MIN_VISIBLE_PCT_HORIZONTAL : 0, pct())}%`,
                      }}
                    />
                  </div>
                  <span class="w-10 shrink-0 text-right text-xs font-semibold tabular-nums">
                    {d.value}
                  </span>
                </div>
              );
            }}
          </For>
        </div>
      </Show>
    </figure>
  );
}
